<template>
  <el-dialog
    :model-value="modelValue"
    :title="confirmMode ? 'ヤマト運輸 送り状情報確認' : 'ヤマト運輸 送り状情報'"
    :width="confirmMode ? '900px' : '650px'"
    class="yamato-dialog"
    @update:model-value="handleClose"
    draggable
  >
    <!-- 入力モード -->
    <el-form
      v-if="!confirmMode"
      ref="yamatoFormRef"
      :model="yamatoForm"
      :rules="yamatoRules"
      label-position="top"
      class="yamato-form"
    >
      <!-- Error message display -->
      <template v-if="Array.isArray(errorMessage)">
        <el-alert
          v-for="(msg, idx) in errorMessage"
          :key="idx"
          :title="msg"
          type="error"
          :closable="false"
          show-icon
          style="margin-bottom: 10px"
        />
      </template>
      <el-alert
        v-else-if="errorMessage"
        :title="errorMessage"
        type="error"
        :closable="false"
        show-icon
        style="margin-bottom: 20px; white-space: pre-line"
      />

      <div class="form-grid">
        <!-- Row 1: 送り状種類 | 出荷予定日 -->
        <el-form-item label="送り状種類" prop="serviceType" required>
          <el-select
            v-model="yamatoForm.serviceType"
            placeholder="選択してください"
            style="width: 100%"
          >
            <el-option label="発払い" value="0" />
            <el-option label="宅急便コンパクト" value="8" />
          </el-select>
        </el-form-item>

        <el-form-item label="出荷予定日" prop="shipDate" required>
          <el-date-picker
            v-model="yamatoForm.shipDate"
            type="date"
            placeholder="yyyy/mm/dd"
            format="YYYY/MM/DD"
            value-format="YYYY/MM/DD"
            :disabled-date="disabledPastDate"
            style="width: 100%"
          />
        </el-form-item>

        <!-- Row 2: お届け予定日 | checkbox 最短日 -->
        <el-form-item
          label="お届け予定日"
          prop="deliveryDate"
          :required="!isShortestDelivery"
        >
          <el-date-picker
            v-model="yamatoForm.deliveryDate"
            type="date"
            placeholder="yyyy/mm/dd"
            format="YYYY/MM/DD"
            value-format="YYYY/MM/DD"
            :disabled="isShortestDelivery"
            :disabled-date="disabledDeliveryDate"
            style="width: 100%"
          />
        </el-form-item>

        <div class="checkbox-item">
          <el-checkbox v-model="isShortestDelivery" @click.native.stop>
            最短日
          </el-checkbox>
        </div>

        <!-- Row 3: 配達時間帯 | お届け先名 -->
        <el-form-item label="配達時間帯" prop="deliveryTimeZone">
          <el-select
            v-model="yamatoForm.deliveryTimeZone"
            placeholder="選択してください"
            :disabled="isShortestDelivery"
            style="width: 100%"
          >
            <el-option label="指定なし" value="" />
            <el-option label="午前中" value="0812" />
            <el-option label="14～16時" value="1416" />
            <el-option label="16～18時" value="1618" />
            <el-option label="18～20時" value="1820" />
            <el-option label="19～21時" value="1921" />
          </el-select>
        </el-form-item>

        <el-form-item
          label="お届け先会社・部門１"
          prop="consigneeDepartment1"
          required
        >
          <el-input v-model="yamatoForm.consigneeDepartment1" />
        </el-form-item>

        <!-- Row 4: 品名 | 荷扱い -->
        <el-form-item label="品名" prop="itemName1" required>
          <el-autocomplete
            v-model="yamatoForm.itemName1"
            :fetch-suggestions="queryItemName"
            placeholder="選択または入力してください"
            style="width: 100%"
            @select="() => yamatoFormRef?.clearValidate('itemName1')"
            @change="() => yamatoFormRef?.clearValidate('itemName1')"
          />
        </el-form-item>

        <el-form-item label="荷扱い" prop="handlingInformation1">
          <el-select
            v-model="yamatoForm.handlingInformation1"
            placeholder="選択してください"
            style="width: 100%"
            clearable
          >
            <el-option label="精密機器" value="精密機器" />
            <el-option label="下積厳禁" value="下積厳禁" />
          </el-select>
        </el-form-item>
      </div>

      <!-- Item 2 fields -->
      <div class="form-grid">
        <el-form-item label="品名２" prop="itemName2">
          <el-autocomplete
            v-model="yamatoForm.itemName2"
            :fetch-suggestions="queryItemName"
            placeholder="選択または入力してください"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="荷扱い２" prop="handlingInformation2">
          <el-select
            v-model="yamatoForm.handlingInformation2"
            placeholder="選択してください"
            style="width: 100%"
            clearable
          >
            <el-option label="精密機器" value="精密機器" />
            <el-option label="下積厳禁" value="下積厳禁" />
          </el-select>
        </el-form-item>
      </div>

      <!-- Full Width -->
      <el-form-item label="記事" class="full-width-item">
        <el-input
          v-model="yamatoForm.note"
          type="textarea"
          :rows="4"
          placeholder="備考や特記事項を入力してください"
        />
      </el-form-item>
    </el-form>

    <!-- 確認モード -->
    <div v-else class="yamato-confirm">
      <div class="confirm-section-container">
        <!-- 基本情報 -->
        <div class="confirm-section">
          <div class="table-title">基本情報</div>
          <BaseDataTable
            :table-data="basicInfoData"
            table-size="small"
            :label-width="140"
          />
        </div>

        <!-- お届け先情報 -->
        <div class="confirm-section">
          <div class="table-title">お届け先情報</div>
          <BaseDataTable
            :table-data="consigneeInfoData"
            table-size="small"
            :label-width="140"
          />
        </div>

        <!-- 品名・荷扱い情報 -->
        <div class="confirm-section">
          <div class="table-title">品名・荷扱い情報</div>
          <BaseDataTable
            :table-data="itemInfoData"
            table-size="small"
            :label-width="140"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <el-button v-if="confirmMode" size="large" @click="confirmMode = false">
        戻る
      </el-button>
      <el-button v-else size="large" @click="handleClose">
        キャンセル
      </el-button>
      <el-button
        size="large"
        type="primary"
        :loading="isSubmitting"
        :disabled="isSubmitting"
        @click="confirmMode ? handleFinalSubmit() : handleSubmit()"
      >
        {{
          confirmMode ? (isSubmitting ? '作成中...' : '送り状を作成') : '確認'
        }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  /* eslint-disable @typescript-eslint/no-explicit-any */
  import { ref, watch, computed, nextTick } from 'vue';
  import type { FormInstance, FormRules } from 'element-plus';
  import { ElMessage } from 'element-plus';
  import type { YamatoInvoiceFormData } from '@/types/yamato';
  import BaseDataTable from '@/components/BaseDataTable.vue';
  import { submitYamatoInvoice } from '@/libs/yamatoApi';
  import { handleAuthorizationError } from '@/libs/apiErrorHandler';

  const props = defineProps<{
    modelValue: boolean;
    initialData?: Partial<YamatoInvoiceFormData>;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'submit', data: YamatoInvoiceFormData): void;
  }>();

  const yamatoFormRef = ref<FormInstance>();
  const confirmMode = ref(false);
  const isShortestDelivery = ref(false);
  const errorMessage = ref<string | string[]>('');

  const yamatoForm = ref<YamatoInvoiceFormData>({
    serviceType: '',
    shipDate: '',
    deliveryDate: '',
    deliveryTimeZone: '',
    consigneeDepartment1: '',
    itemName1: '',
    handlingInformation1: '',
    note: ''
  });

  // フォームバリデーションルール
  const yamatoRules = computed<FormRules>(() => ({
    serviceType: [
      { required: true, trigger: 'blur', message: '送り状種類は必要です。' }
    ],
    shipDate: [
      { required: true, trigger: 'blur', message: '出荷予定日は必要です。' }
    ],
    deliveryDate: [
      {
        required: !isShortestDelivery.value,
        trigger: 'blur',
        message: 'お届け予定日は必要です。'
      }
    ],
    consigneeDepartment1: [
      {
        required: true,
        trigger: 'blur',
        message: 'お届け先会社・部門１は必要です。'
      }
    ],
    itemName1: [
      {
        required: true,
        trigger: ['blur', 'change'],
        message: '品名は必要です。'
      }
    ]
  }));

  /**
   * Helper: 送り状種類コードをラベルに変換
   */
  const getServiceTypeLabel = (code: string): string => {
    const labels: Record<string, string> = {
      '0': '発払い',
      '8': '宅急便コンパクト'
    };
    return labels[code] || code;
  };

  /**
   * Helper: 配達時間帯コードをラベルに変換
   */
  const getDeliveryTimeLabel = (code: string): string => {
    const labels: Record<string, string> = {
      '': '指定なし',
      '0812': '午前中',
      '1416': '14～16時',
      '1618': '16～18時',
      '1820': '18～20時',
      '1921': '19～21時'
    };
    return labels[code] || code;
  };

  /**
   * Item name options for autocomplete
   */
  const itemNameOptions = [
    'MCCS Type T 台セット',
    'MCCS Type T キーカット用ハーネス',
    'MCCS Type T プッシュ用ハーネス',
    'MCCSⅡ',
    'MCCSⅢ',
    'AILS用 MCCSⅢ',
    'Secual用 MCCSⅢ',
    'SMART BLOCKER ベースキット／専用ハーネスセット'
  ];

  /**
   * Query function for item name autocomplete
   */
  const queryItemName = (
    queryString: string,
    cb: (results: Array<{ value: string }>) => void
  ) => {
    const results = queryString
      ? itemNameOptions
          .filter((item) =>
            item.toLowerCase().includes(queryString.toLowerCase())
          )
          .map((item) => ({ value: item }))
      : itemNameOptions.map((item) => ({ value: item }));
    cb(results);
  };

  /**
   * Disable past dates for 出荷予定日
   */
  const disabledPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
  };

  /**
   * Disable dates before 出荷予定日 + 1 days for お届け予定日
   */
  const disabledDeliveryDate = (date: Date) => {
    if (!yamatoForm.value.shipDate) {
      return disabledPastDate(date);
    }
    const shipDate = new Date(yamatoForm.value.shipDate);
    shipDate.setHours(0, 0, 0, 0);
    const minDeliveryDate = new Date(shipDate);
    minDeliveryDate.setDate(minDeliveryDate.getDate() + 1);
    return date.getTime() < minDeliveryDate.getTime();
  };

  watch(isShortestDelivery, (newValue) => {
    if (newValue) {
      yamatoForm.value.deliveryDate = '';
      nextTick(() => {
        yamatoFormRef.value?.clearValidate('deliveryDate');
      });
    }
  });

  /**
   * Computed properties for table data
   */
  const basicInfoData = computed(() => {
    const data = [
      {
        label: '送り状種類',
        value: getServiceTypeLabel(yamatoForm.value.serviceType)
      },
      {
        label: 'クール区分',
        value:
          yamatoForm.value.isCool === '0'
            ? '通常'
            : yamatoForm.value.isCool === '1'
              ? 'クール冷凍'
              : yamatoForm.value.isCool === '2'
                ? 'クール冷蔵'
                : '-'
      },
      { label: '出荷予定日', value: yamatoForm.value.shipDate || '-' },
      {
        label: 'お届け予定日',
        value: isShortestDelivery.value
          ? '最短日'
          : yamatoForm.value.deliveryDate || '-'
      },
      {
        label: '配達時間帯',
        value: getDeliveryTimeLabel(yamatoForm.value.deliveryTimeZone)
      }
    ];

    // センターサービス情報
    data.push({
      label: '止置き',
      value:
        yamatoForm.value.isUsingCenterService === '1'
          ? '使用する'
          : '使用しない'
    });
    if (
      yamatoForm.value.isUsingCenterService === '1' &&
      yamatoForm.value.consigneeCenterCode
    ) {
      data.push({
        label: '営業所コード',
        value: yamatoForm.value.consigneeCenterCode
      });
    }

    // 発行情報
    if (yamatoForm.value.packageQty) {
      data.push({ label: '発行枚数', value: yamatoForm.value.packageQty });
    }
    if (yamatoForm.value.isPrintingLot) {
      const lotLabel =
        yamatoForm.value.isPrintingLot === '1'
          ? '印字する'
          : yamatoForm.value.isPrintingLot === '2'
            ? '印字しない'
            : '枠ごと印字';
      data.push({ label: '個数口表示', value: lotLabel });
    }

    return data;
  });

  const consigneeInfoData = computed(() => {
    const data = [
      {
        label: 'お届け先\n会社・部門１',
        value: yamatoForm.value.consigneeDepartment1 || '-'
      },
      { label: '担当者', value: yamatoForm.value.consigneeName || '-' }
    ];
    if (yamatoForm.value.consigneeNameKana) {
      data.push({
        label: '担当者(ｶﾅ)',
        value: yamatoForm.value.consigneeNameKana
      });
    }
    if (yamatoForm.value.consigneeTitle) {
      data.push({ label: '敬称', value: yamatoForm.value.consigneeTitle });
    }
    data.push(
      {
        label: '電話番号',
        value: yamatoForm.value.consigneeTelephoneDisplay || '-'
      },
      { label: '郵便番号', value: yamatoForm.value.consigneeZipCode || '-' },
      {
        label: '住所',
        value:
          `${yamatoForm.value.consigneeAddress1 || ''}${yamatoForm.value.consigneeAddress2 || ''}${yamatoForm.value.consigneeAddress3 || ''}` ||
          '-'
      }
    );
    if (yamatoForm.value.consigneeAddress4) {
      data.push({
        label: '住所サブ',
        value: yamatoForm.value.consigneeAddress4
      });
    }
    if (yamatoForm.value.consigneeDepartment2) {
      data.push({
        label: '会社・部門２',
        value: yamatoForm.value.consigneeDepartment2
      });
    }
    return data;
  });

  const itemInfoData = computed(() => {
    const data = [
      { label: '品名１', value: yamatoForm.value.itemName1 || '-' },
      {
        label: '荷扱い１',
        value: yamatoForm.value.handlingInformation1 || '-'
      }
    ];
    if (yamatoForm.value.itemName2) {
      data.push({ label: '品名２', value: yamatoForm.value.itemName2 });
    }
    if (yamatoForm.value.handlingInformation2) {
      data.push({
        label: '荷扱い２',
        value: yamatoForm.value.handlingInformation2
      });
    }
    if (yamatoForm.value.note) {
      data.push({ label: '記事', value: yamatoForm.value.note });
    }
    return data;
  });

  /**
   * ダイアログを閉じる処理
   */
  const handleClose = () => {
    emit('update:modelValue', false);
    confirmMode.value = false;
    isShortestDelivery.value = false;
    errorMessage.value = '';
    resetForm();
  };

  /**
   * フォームをリセット
   */
  const resetForm = () => {
    yamatoFormRef.value?.resetFields();
    yamatoForm.value = {
      serviceType: '',
      shipDate: '',
      deliveryDate: '',
      deliveryTimeZone: '',
      consigneeDepartment1: '',
      itemName1: '',
      handlingInformation1: '',
      itemName2: '',
      handlingInformation2: '',
      note: ''
    };
  };

  /**
   * 確認モードへ移行
   */
  const handleSubmit = async () => {
    if (!yamatoFormRef.value) return;

    await yamatoFormRef.value.validate((valid) => {
      if (!valid) {
        return;
      }
      confirmMode.value = true;
    });
  };

  const isSubmitting = ref(false);

  /**
   * 最終送信処理
   */
  const handleFinalSubmit = async () => {
    if (isSubmitting.value) return;

    try {
      isSubmitting.value = true;
      errorMessage.value = '';

      const normalizedConsigneeName =
        yamatoForm.value.consigneeName?.trim().replace(/様$/, '').trim() || '';

      await submitYamatoInvoice({
        ...yamatoForm.value,
        consigneeName: normalizedConsigneeName
      });

      ElMessage.success('ヤマト運輸送り状を作成しました');
      emit('submit', yamatoForm.value);

      handleClose();
    } catch (error: any) {
      if (error.response?.status === 401) {
        await handleAuthorizationError();
        return;
      }
      const errorMsg = error?.message || '';
      const statusCode: number | undefined =
        typeof error?.statusCode === 'number' ? error.statusCode : undefined;
      const isYamatoApiError = error?.error === 'Yamato API error';

      const errorDescriptions: string[] = Array.isArray(error?.errors)
        ? error.errors
            .map((e: any) => `[${e?.error_code}] ${e?.error_description}`)
            .filter((s: any) => typeof s === 'string' && s.length > 0)
        : [];

      const errorPayload: string | string[] =
        errorDescriptions.length > 0 ? errorDescriptions : errorMsg;

      const is400Error = statusCode === 400 || isYamatoApiError;

      if (is400Error) {
        confirmMode.value = false;
        errorMessage.value =
          errorPayload.length > 0 ? errorPayload : 'エラーが発生しました';
        nextTick(() => {
          const formElement = document.querySelector('.yamato-form');
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      } else {
        ElMessage.error({
          message: error.message || 'エラーが発生しました',
          duration: 5000,
          showClose: true
        });
      }
      console.error('Yamato API Error:', error);
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * 翌日を計算（土日を問わず）
   * @returns 翌日の日付文字列 (YYYY/MM/DD形式)
   */
  const getNextDay = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  /**
   * 初期データをフォームに反映
   * 着指定日あり → デフォルト値にする（既存の値を保持）
   * 着指定日なし → デフォルト値は翌日にする（土日を問わず）
   * 初期データなし → 空にする
   */
  const applyInitialData = () => {
    if (!props.modelValue) return;
    if (props.initialData) {
      // 着指定日あり → デフォルト値にする（既存の値を保持）
      // 着指定日なし → デフォルト値は翌日にする（土日を問わず）
      const deliveryDate = props.initialData.deliveryDate
        ? props.initialData.deliveryDate
        : getNextDay();
      yamatoForm.value = {
        ...yamatoForm.value,
        ...props.initialData,
        deliveryDate
      };
    } else {
      // ▼現在: 空（初期データがない場合は空にする）
      yamatoForm.value.deliveryDate = '';
    }
  };

  /**
   * 初期データを監視してフォームに反映
   */
  watch(
    () => props.initialData,
    () => {
      applyInitialData();
    },
    { immediate: true, deep: true }
  );

  /**
   * ダイアログが開いた時に初期データを反映
   */
  watch(
    () => props.modelValue,
    (isOpen) => {
      if (isOpen) {
        applyInitialData();
      }
    }
  );
</script>

<style lang="scss">
  .yamato-dialog {
    .yamato-form {
      .form-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0 20px;
      }

      .checkbox-item {
        display: flex;
        margin-top: 30px;
        height: 100%;
      }
    }

    :deep(.el-form-item) {
      margin-bottom: 18px;

      &.full-width-item {
        grid-column: 1 / -1;
      }
    }

    :deep(.el-form-item__label) {
      font-size: 13px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 6px;
      padding: 0;
    }

    :deep(
      .el-form-item.is-required:not(.is-no-asterisk)
        > .el-form-item__label:before
    ) {
      content: '*';
      color: #f56c6c;
      margin-right: 4px;
    }

    :deep(.el-input),
    :deep(.el-select),
    :deep(.el-date-editor) {
      width: 100% !important;
    }

    :deep(.el-input__wrapper) {
      width: 100%;
    }

    :deep(.el-input__inner),
    :deep(.el-textarea__inner) {
      font-size: 14px;
      width: 100%;
    }

    :deep(.el-select .el-input) {
      width: 100% !important;
    }

    :deep(.el-select .el-input__wrapper) {
      width: 100% !important;
    }

    .yamato-confirm {
      max-height: 600px;
      overflow-y: auto;
      padding: 5px 10px;

      .confirm-section-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
      }

      .confirm-section {
        .table-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          margin: 5px 0;
        }

        .BaseDataTable {
          width: 100%;
          margin: 0 auto;
        }
      }
    }
  }
</style>
